import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ShoppingCart, ChefHat, Trophy, Trash2, Check, Plus, Edit2, Calendar, LogOut, X, Filter, TrendingUp, Eye, EyeOff, Minus, Volume2, VolumeX, XCircle } from 'lucide-react';

// 🔧 FIX: API_URL dinamico - usa /api in produzione, localhost:8000/api in dev
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:8000/api';

// 🆕 Tipi esclusi dallo sconto 10%
const TIPI_ESCLUSI_SCONTO = ['bibita', 'dolci'];

// 🆕 Helper: calcola totale con sconto opzionale del 10% (esclude bibite e dolci)
const calcolaTotaleConSconto = (carrello, scontoAttivo) => {
  if (!scontoAttivo) {
    return carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0);
  }
  return carrello.reduce((sum, item) => {
    const tipoLower = (item.tipo || '').toLowerCase();
    const esclusoDaSconto = TIPI_ESCLUSI_SCONTO.includes(tipoLower);
    const prezzoEffettivo = esclusoDaSconto ? item.prezzo : item.prezzo * 0.9;
    return sum + (prezzoEffettivo * item.quantita);
  }, 0);
};

// Componente isolato per il nome ordine
const NomeOrdineInput = React.memo(({ value, onUpdate }) => {
  const [localValue, setLocalValue] = React.useState(value || '');
  const isFirstRender = React.useRef(true);

  if (isFirstRender.current) {
    if (value && localValue === '') {
      setLocalValue(value);
    }
    isFirstRender.current = false;
  }

  React.useEffect(() => {
    if (value === '') {
      setLocalValue('');
    }
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onUpdate(localValue);
    }
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  return (
    <input
      type="text"
      placeholder="Nome ordine (opzionale, es. Tavolo 5, Mario)"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  );
});

// Componente ottimizzato per singolo alimento
const AlimentoItem = React.memo(({ alimento, onAggiungi }) => {
  return (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-semibold text-lg">{alimento.nome}</h3>
          <span className="text-sm text-gray-500">{alimento.tipo}</span>
        </div>
        <span className="text-lg font-bold text-green-600">€{alimento.prezzo.toFixed(2)}</span>
      </div>
      {alimento.ingredienti && alimento.ingredienti.length > 0 && (
        <p className="text-sm text-gray-600 mb-3">{alimento.ingredienti.join(', ')}</p>
      )}
      <button
        onClick={() => onAggiungi(alimento)}
        className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={18} /> Aggiungi
      </button>
    </div>
  );
});

// 🎯 SOLUZIONE FINALE: Componente completamente autonomo che renderizza la lista
const MenuAlimentiConRicerca = React.memo(({ alimenti, filtroTipo, onAggiungi }) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  // Filtra localmente in tempo reale - NESSUNA comunicazione con parent
  const alimentiFiltrati = React.useMemo(() => {
    let filtrati = alimenti;
    
    // Filtro per tipo
    if (filtroTipo !== 'Tutti') {
      filtrati = filtrati.filter(a => a.tipo.toLowerCase() === filtroTipo.toLowerCase());
    }
    
    // Filtro per ricerca nome
    if (searchTerm.trim() !== '') {
      const termineRicerca = searchTerm.toLowerCase().trim();
      filtrati = filtrati.filter(a => 
        a.nome.toLowerCase().includes(termineRicerca)
      );
    }
    
    return filtrati.sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
  }, [alimenti, filtroTipo, searchTerm]);

  const handleClear = () => {
    setSearchTerm('');
  };

  return (
    <div>
      {/* Campo di ricerca */}
      <div className="space-y-2 mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Cerca alimento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            autoComplete="off"
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          {searchTerm && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
        
        {/* Contatore risultati */}
        {(searchTerm || filtroTipo !== 'Tutti') && (
          <div className="text-sm text-gray-600">
            {alimentiFiltrati.length} {alimentiFiltrati.length === 1 ? 'risultato' : 'risultati'}
            {searchTerm && ` per "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Lista alimenti filtrati */}
      <div className="space-y-3 max-h-96 overflow-y-auto overflow-x-hidden" style={{WebkitOverflowScrolling: 'touch'}}>
        {alimentiFiltrati.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg mb-2">Nessun alimento trovato</p>
            {searchTerm && (
              <p className="text-sm">
                Prova con un'altra parola chiave
              </p>
            )}
          </div>
        ) : (
          alimentiFiltrati.map((alimento) => (
            <AlimentoItem 
              key={alimento.id} 
              alimento={alimento} 
              onAggiungi={onAggiungi}
            />
          ))
        )}
      </div>
    </div>
  );
});

// 🆕 Componente isolato per ogni item del carrello CON QUANTITÀ
const CarrelloItem = React.memo(({ item, onRemove, onUpdateNote, onUpdatePrezzo, onUpdateQuantita, isAdmin, scontoAttivo }) => {
  const [localNote, setLocalNote] = React.useState(item.note || '');
  const [localPrezzo, setLocalPrezzo] = React.useState(item.prezzo.toFixed(2));
  const [editingPrezzo, setEditingPrezzo] = React.useState(false);

  const handleBlur = () => {
    if (localNote !== item.note) {
      onUpdateNote(item.tempId, localNote);
    }
  };

  const handlePrezzoBlur = () => {
    const nuovoPrezzo = parseFloat(localPrezzo);
    if (!isNaN(nuovoPrezzo) && nuovoPrezzo > 0 && nuovoPrezzo !== item.prezzo) {
      onUpdatePrezzo(item.tempId, nuovoPrezzo);
    } else {
      setLocalPrezzo(item.prezzo.toFixed(2));
    }
    setEditingPrezzo(false);
  };

  const handlePrezzoKeyDown = (e) => {
    if (e.key === 'Enter') {
      handlePrezzoBlur();
    }
  };

  const handleChange = (e) => {
    setLocalNote(e.target.value);
  };

  const handlePrezzoChange = (e) => {
    setLocalPrezzo(e.target.value);
  };

  const totaleRiga = item.prezzo * item.quantita;

  return (
    <div className="border-b pb-3">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <span className="font-semibold">{item.nome}</span>
          {item.quantita > 1 && (
            <span className="ml-2 text-sm text-gray-600">x{item.quantita}</span>
          )}
          {/* 🆕 Badge sconto applicato */}

        </div>
        <div className="flex items-center gap-2">
          {isAdmin && editingPrezzo ? (
            <input
              type="number"
              step="0.01"
              value={localPrezzo}
              onChange={handlePrezzoChange}
              onBlur={handlePrezzoBlur}
              onKeyDown={handlePrezzoKeyDown}
              className="w-16 text-right text-green-600 font-bold border rounded px-1"
              autoFocus
            />
          ) : (
            <span 
              className={`font-bold text-green-600 ${isAdmin ? 'cursor-pointer hover:bg-green-50 px-1 rounded' : ''}`}
              onClick={() => isAdmin && setEditingPrezzo(true)}
              title={isAdmin ? "Clicca per modificare" : ""}
            >
              {item.quantita > 1
                ? `€${item.prezzo.toFixed(2)} (€${totaleRiga.toFixed(2)})`
                : `€${item.prezzo.toFixed(2)}`}
            </span>
          )}
          <button
            onClick={() => onRemove(item.tempId)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      {/* 🆕 Controlli quantità */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => onUpdateQuantita(item.tempId, Math.max(1, item.quantita - 1))}
          className="bg-gray-200 hover:bg-gray-300 rounded p-1 transition"
          disabled={item.quantita <= 1}
        >
          <Minus size={16} />
        </button>
        <span className="min-w-[3rem] text-center font-semibold">{item.quantita}</span>
        <button
          onClick={() => onUpdateQuantita(item.tempId, item.quantita + 1)}
          className="bg-green-600 hover:bg-green-700 text-white rounded p-1 transition"
        >
          <Plus size={16} />
        </button>
      </div>

      <input
        type="text"
        placeholder="Note (es. senza salsa, ben cotto...)"
        value={localNote}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full text-sm border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-green-300"
      />
    </div>
  );
});

// 🆕 Componente per item in modifica CON QUANTITÀ
const ModificaCarrelloItem = React.memo(({ item, onRemove, onUpdateNote, onUpdatePrezzo, onUpdateQuantita, scontoAttivo }) => {
  const [localNote, setLocalNote] = React.useState(item.note || '');
  const [localPrezzo, setLocalPrezzo] = React.useState(item.prezzo.toFixed(2));
  const [editingPrezzo, setEditingPrezzo] = React.useState(false);

  const handleBlur = () => {
    if (localNote !== item.note) {
      onUpdateNote(item.tempId, localNote);
    }
  };

  const handlePrezzoBlur = () => {
    const nuovoPrezzo = parseFloat(localPrezzo);
    if (!isNaN(nuovoPrezzo) && nuovoPrezzo > 0 && nuovoPrezzo !== item.prezzo) {
      onUpdatePrezzo(item.tempId, nuovoPrezzo);
    } else {
      setLocalPrezzo(item.prezzo.toFixed(2));
    }
    setEditingPrezzo(false);
  };

  const handlePrezzoKeyDown = (e) => {
    if (e.key === 'Enter') {
      handlePrezzoBlur();
    }
  };

  const handleChange = (e) => {
    setLocalNote(e.target.value);
  };

  const handlePrezzoChange = (e) => {
    setLocalPrezzo(e.target.value);
  };

  const totaleRiga = item.prezzo * item.quantita;

  return (
    <div className="border-b pb-2">
      <div className="flex justify-between items-start mb-1">
        <div className="flex-1">
          <span className="font-semibold">{item.nome}</span>
          {item.quantita > 1 && (
            <span className="ml-2 text-sm text-gray-600">x{item.quantita}</span>
          )}

        </div>
        <div className="flex items-center gap-2">
          {editingPrezzo ? (
            <input
              type="number"
              step="0.01"
              value={localPrezzo}
              onChange={handlePrezzoChange}
              onBlur={handlePrezzoBlur}
              onKeyDown={handlePrezzoKeyDown}
              className="w-16 text-right text-green-600 border rounded px-1"
              autoFocus
            />
          ) : (
            <span 
              className="cursor-pointer hover:bg-green-50 px-1 rounded text-sm text-green-600" 
              onClick={() => setEditingPrezzo(true)}
              title="Clicca per modificare"
            >
              {item.quantita > 1
                ? `€${item.prezzo.toFixed(2)} (€${totaleRiga.toFixed(2)})`
                : `€${item.prezzo.toFixed(2)}`}
            </span>
          )}
          <button
            onClick={() => onRemove(item.tempId)}
            className="text-red-500"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {/* 🆕 Controlli quantità */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => onUpdateQuantita(item.tempId, Math.max(1, item.quantita - 1))}
          className="bg-gray-200 hover:bg-gray-300 rounded p-1 transition text-sm"
          disabled={item.quantita <= 1}
        >
          <Minus size={14} />
        </button>
        <span className="min-w-[2.5rem] text-center font-semibold text-sm">{item.quantita}</span>
        <button
          onClick={() => onUpdateQuantita(item.tempId, item.quantita + 1)}
          className="bg-green-600 hover:bg-green-700 text-white rounded p-1 transition text-sm"
        >
          <Plus size={14} />
        </button>
      </div>

      <input
        type="text"
        placeholder="Note..."
        value={localNote}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-300"
      />
    </div>
  );
});

// Componente per totale modificabile nel modal
const ModificaTotaleOrdine = React.memo(({ totale, onUpdate }) => {
  const [editing, setEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(totale.toFixed(2));

  React.useEffect(() => {
    if (!editing) {
      setLocalValue(totale.toFixed(2));
    }
  }, [totale, editing]);

  const handleBlur = () => {
    const nuovoTotale = parseFloat(localValue);
    if (!isNaN(nuovoTotale) && nuovoTotale >= 0) {
      onUpdate(nuovoTotale);
    } else {
      setLocalValue(totale.toFixed(2));
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        step="0.01"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="text-lg font-bold text-green-600 border-2 border-green-500 rounded px-2 py-1 w-32 text-right"
        autoFocus
      />
    );
  };

  return (
    <span
      onClick={() => setEditing(true)}
      className="text-green-600 cursor-pointer hover:bg-green-50 px-2 py-1 rounded"
      title="Clicca per modificare manualmente"
    >
      €{totale.toFixed(2)}
    </span>
  );
});

export default function FarcitoApp() {
  const [view, setView] = useState('ordini');
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [alimenti, setAlimenti] = useState([]);
  const [carrello, setCarrello] = useState([]);
  const [nomeOrdine, setNomeOrdine] = useState('');
  const [daAsporto, setDaAsporto] = useState(false);
  const [metodoPagamento, setMetodoPagamento] = useState('contante');
  // 🆕 Stato sconto 10% (solo admin)
  const [scontoDecimi, setScontoDecimi] = useState(false);
  const [ordiniGiorno, setOrdiniGiorno] = useState([]);
  const [dataSelezionata, setDataSelezionata] = useState(new Date().toISOString().split('T')[0]);
  const [dateDisponibili, setDateDisponibili] = useState([]);
  const [ordineInModifica, setOrdineInModifica] = useState(null);
  const [error, setError] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('Tutti');
  const [statistiche, setStatistiche] = useState([]);
  const [periodoStats, setPeriodoStats] = useState('settimana');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [nascondiCompletati, setNascondiCompletati] = useState(false);
  const [alimentiPiuVenduti, setAlimentiPiuVenduti] = useState([]);
  const [vistaStats, setVistaStats] = useState('dettaglio');
  const [invioInCorso, setInvioInCorso] = useState(false);
  // 🔔 Notifica sonora nuovi ordini in cucina
  const [suonoAbilitato, setSuonoAbilitato] = useState(() => {
    return localStorage.getItem('farcito_suono') !== 'off';
  });
  const audioCtxRef = useRef(null);
  const ordiniNotiRef = useRef(new Set());
  const primoCaricamentoRef = useRef

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      * {
        -webkit-tap-highlight-color: transparent;
      }
      .overflow-y-auto {
        -webkit-overflow-scrolling: touch;
        transform: translateZ(0);
        backface-visibility: hidden;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    const savedUser = localStorage.getItem('farcito_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        
        if (userData.token) {
          fetch(`${API_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${userData.token}`
            }
          })
          .then(res => {
            if (res.ok) {
              setUser(userData);
            } else {
              console.log('Token non valido o scaduto');
              localStorage.removeItem('farcito_user');
            }
          })
          .catch(error => {
            console.error('Errore verifica token:', error);
            localStorage.removeItem('farcito_user');
          });
        } else {
          localStorage.removeItem('farcito_user');
        }
      } catch (error) {
        console.error('Errore nel recupero utente salvato:', error);
        localStorage.removeItem('farcito_user');
      }
    }
  }, []);

  const authHeader = useMemo(() => {
    if (!user || !user.token) return {};
    return { 'Authorization': `Bearer ${user.token}` };
  }, [user]);

  // 🔔 Campanella a due note generata via Web Audio (nessun file audio da caricare)
  const riproduciSuonoNuovoOrdine = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtx();
      }
      const ctx = audioCtxRef.current;
      // I browser sospendono il contesto finché non c'è un'interazione utente
      if (ctx.state === 'suspended') ctx.resume();

      const suonaNota = (frequenza, inizio, durata) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = frequenza;
        gain.gain.setValueAtTime(0, ctx.currentTime + inizio);
        gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + inizio + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + inizio + durata);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + inizio);
        osc.stop(ctx.currentTime + inizio + durata);
      };

      suonaNota(880, 0, 0.25);
      suonaNota(1174.66, 0.18, 0.35);
    } catch (error) {
      console.error('Errore riproduzione suono:', error);
    }
  }, []);

  // Sblocca l'audio al primo tocco/click: senza gesto utente il browser blocca la riproduzione
  useEffect(() => {
    const sbloccaAudio = () => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtx();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener('pointerdown', sbloccaAudio, { once: true });
    return () => document.removeEventListener('pointerdown', sbloccaAudio);
  }, []);

  // Cambiando vista o data la lista di riferimento non è più valida: riparte da zero
  useEffect(() => {
    primoCaricamentoRef.current = true;
    ordiniNotiRef.current = new Set();
  }, [view, dataSelezionata]);

  // 🔔 Confronta gli ordini appena caricati con quelli già visti e segnala i nuovi
  useEffect(() => {
    if (view !== 'cucina') return;

    const numeriAttuali = new Set(ordiniGiorno.map(o => o.numero_ordine));

    // Al primo caricamento memorizza soltanto: gli ordini esistenti non sono "nuovi"
    if (primoCaricamentoRef.current) {
      ordiniNotiRef.current = numeriAttuali;
      primoCaricamentoRef.current = false;
      return;
    }

    const nuoviOrdini = ordiniGiorno.filter(
      o => !ordiniNotiRef.current.has(o.numero_ordine) && !o.completato && !o.cancellato
    );
    ordiniNotiRef.current = numeriAttuali;

    const oggi = new Date().toISOString().split('T')[0];
    if (nuoviOrdini.length > 0 && suonoAbilitato && dataSelezionata === oggi) {
      riproduciSuonoNuovoOrdine();
    }
  }, [ordiniGiorno, view, dataSelezionata, suonoAbilitato, riproduciSuonoNuovoOrdine]);

  const toggleSuono = (abilitato) => {
    setSuonoAbilitato(abilitato);
    localStorage.setItem('farcito_suono', abilitato ? 'on' : 'off');
    // Il click è il gesto utente che sblocca l'audio, e serve da conferma sonora
    if (abilitato) riproduciSuonoNuovoOrdine();
  };

  const fetchAlimentiPiuVenduti = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    
    const oggi = new Date();
    let dataInizio, dataFine;
    
    if (periodoStats === 'giorno') {
      dataInizio = dataFine = oggi.toISOString().split('T')[0];
    } else if (periodoStats === 'settimana') {
      const settimanaFa = new Date(oggi);
      settimanaFa.setDate(oggi.getDate() - 6);
      dataInizio = settimanaFa.toISOString().split('T')[0];
      dataFine = oggi.toISOString().split('T')[0];
    } else {
      const meseFa = new Date(oggi);
      meseFa.setDate(oggi.getDate() - 29);
      dataInizio = meseFa.toISOString().split('T')[0];
      dataFine = oggi.toISOString().split('T')[0];
    }
    
    try {
      const res = await fetch(
        `${API_URL}/statistiche/alimenti-piu-venduti?data_inizio=${dataInizio}&data_fine=${dataFine}`,
        { headers: authHeader }
      );
      if (res.ok) {
        const data = await res.json();
        setAlimentiPiuVenduti(data.alimenti);
      }
    } catch (error) {
      console.error('Errore caricamento alimenti più venduti:', error);
    }
  }, [user, authHeader, periodoStats]);
  
  const fetchAlimenti = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/alimenti`, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setAlimenti(data);
      }
    } catch (error) {
      console.error('Errore caricamento alimenti:', error);
    }
  }, [user, authHeader]);

  const fetchOrdini = useCallback(async () => {
    if (!user) return;
    try {
      const url = user.role === 'admin' && dataSelezionata !== new Date().toISOString().split('T')[0]
        ? `${API_URL}/ordini?data=${dataSelezionata}`
        : `${API_URL}/ordini`;
      
      const res = await fetch(url, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setOrdiniGiorno(data.ordini || []);
      }
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
    }
  }, [user, authHeader, dataSelezionata]);

  const fetchDateDisponibili = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    try {
      const res = await fetch(`${API_URL}/ordini/date-disponibili`, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setDateDisponibili(data.date);
      }
    } catch (error) {
      console.error('Errore caricamento date:', error);
    }
  }, [user, authHeader]);

  const fetchStatistiche = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    
    const oggi = new Date();
    let dataInizio, dataFine;
    
    if (periodoStats === 'giorno') {
      dataInizio = dataFine = oggi.toISOString().split('T')[0];
    } else if (periodoStats === 'settimana') {
      const settimanaFa = new Date(oggi);
      settimanaFa.setDate(oggi.getDate() - 6);
      dataInizio = settimanaFa.toISOString().split('T')[0];
      dataFine = oggi.toISOString().split('T')[0];
    } else {
      const meseFa = new Date(oggi);
      meseFa.setDate(oggi.getDate() - 29);
      dataInizio = meseFa.toISOString().split('T')[0];
      dataFine = oggi.toISOString().split('T')[0];
    }
    
    try {
      const res = await fetch(
        `${API_URL}/statistiche/giornaliere?data_inizio=${dataInizio}&data_fine=${dataFine}`,
        { headers: authHeader }
      );
      if (res.ok) {
        const data = await res.json();
        setStatistiche(data.statistiche);
      }
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  }, [user, authHeader, periodoStats]);

  useEffect(() => {
    if (ordineInModifica) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [ordineInModifica]);

  useEffect(() => {
    if (user) {
      fetchAlimenti();
      if (view === 'cucina') {
        fetchOrdini();
        if (user.role === 'admin') {
          fetchDateDisponibili();
        }
        if (!ordineInModifica) {
          const interval = setInterval(fetchOrdini, 5000);
          return () => clearInterval(interval);
        }
      } else if (view === 'statistiche' && user.role === 'admin') {
        if (vistaStats === 'dettaglio') {
              fetchStatistiche();
          } else {
        fetchAlimentiPiuVenduti();
        }
      }
    }
  }, [view, user, dataSelezionata, periodoStats, ordineInModifica, vistaStats, fetchAlimenti, fetchOrdini, fetchDateDisponibili, fetchStatistiche, fetchAlimentiPiuVenduti]);

  const login = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const userData = {
          username: data.user.username,
          role: data.user.role,
          token: data.access_token
        };
        setUser(userData);
        localStorage.setItem('farcito_user', JSON.stringify(userData));
        setLoginForm({ username: '', password: '' });
      } else {
        const errorData = await res.json();
        setError(errorData.detail || 'Credenziali non valide');
      }
    } catch (error) {
      console.error('Errore login:', error);
      setError('Errore di connessione al server');
    }
  };

  const logout = () => {
    setUser(null);
    setCarrello([]);
    setOrdiniGiorno([]);
    setView('ordini');
    setScontoDecimi(false);
    localStorage.removeItem('farcito_user');
  };

  // Funzione migliorata: aggiunge al carrello
  const aggiungiAlCarrello = useCallback((alimento) => {
    setCarrello(prev => {
      return [...prev, { ...alimento, note: '', quantita: 1, tempId: Date.now() }];
    });
  }, []);

  const rimuoviDalCarrello = (tempId) => {
    setCarrello(carrello.filter(item => item.tempId !== tempId));
  };

  const aggiornaNote = (tempId, note) => {
    setCarrello(prevCarrello => prevCarrello.map(item => 
      item.tempId === tempId ? { ...item, note } : item
    ));
  };

  const aggiornaPrezzo = (tempId, prezzo) => {
    setCarrello(prevCarrello => prevCarrello.map(item => 
      item.tempId === tempId ? { ...item, prezzo } : item
    ));
  };

  const aggiornaQuantita = (tempId, quantita) => {
    if (quantita < 1) return;
    setCarrello(prevCarrello => prevCarrello.map(item => 
      item.tempId === tempId ? { ...item, quantita } : item
    ));
  };

  const inviaOrdine = async () => {
    if (carrello.length === 0) {
      alert('Aggiungi almeno un alimento all\'ordine');
      return;
    }
  
    if (invioInCorso) return;
    setInvioInCorso(true);
  
    // 🆕 Calcola totale con eventuale sconto
    const totale = calcolaTotaleConSconto(carrello, scontoDecimi);
    
    // 🆕 Espandi alimenti applicando il prezzo scontato se necessario
    const alimentiEspansi = [];
    carrello.forEach(item => {
      for (let i = 0; i < item.quantita; i++) {
        alimentiEspansi.push({
          alimento_id: item.id,
          nome: item.nome,
          prezzo: item.prezzo,
          tipo: item.tipo,  // Salva il tipo per calcolo sconto futuro
          note: item.note || ''
        });
      }
    });
  
    const ordine = {
      alimenti: alimentiEspansi,
      totale,
      nome_ordine: nomeOrdine.trim(),
      da_asporto: daAsporto,
      metodo_pagamento: metodoPagamento,
      sconto_applicato: scontoDecimi  // 🆕 Salva flag sconto nel DB
    };
  
    try {
      const res = await fetch(`${API_URL}/ordini`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(ordine)
      });
      
      if (res.ok) {
        setCarrello([]);
        setNomeOrdine('');
        setDaAsporto(false);
        setMetodoPagamento('contante');
        setScontoDecimi(false); // 🆕 reset sconto dopo invio
        if (view === 'cucina') fetchOrdini();
      } else {
        alert('Errore durante l\'invio dell\'ordine');
      }
    } catch (error) {
      console.error('Errore invio ordine:', error);
      alert('Errore durante l\'invio dell\'ordine');
    } finally {
      setInvioInCorso(false);
    }
  };

  const completaOrdine = async (numeroOrdine) => {
    try {
      const url = dataSelezionata !== new Date().toISOString().split('T')[0]
        ? `${API_URL}/ordini/${numeroOrdine}/completa?data=${dataSelezionata}`
        : `${API_URL}/ordini/${numeroOrdine}/completa`;
      
      const res = await fetch(url, {
        method: 'PATCH',
        headers: authHeader
      });
      if (res.ok) fetchOrdini();
      else alert('Errore durante il completamento dell\'ordine');
    } catch (error) {
      console.error('Errore completamento ordine:', error);
      alert('Errore durante il completamento dell\'ordine');
    }
  };

  const riapriOrdine = async (numeroOrdine) => {
    try {
      const url = dataSelezionata !== new Date().toISOString().split('T')[0]
        ? `${API_URL}/ordini/${numeroOrdine}/riapri?data=${dataSelezionata}`
        : `${API_URL}/ordini/${numeroOrdine}/riapri`;
      
      const res = await fetch(url, {
        method: 'PATCH',
        headers: authHeader
      });
      if (res.ok) fetchOrdini();
      else alert('Errore durante la riapertura dell\'ordine');
    } catch (error) {
      console.error('Errore riapertura ordine:', error);
      alert('Errore durante la riapertura dell\'ordine');
    }
  };

  const eliminaOrdine = async (numeroOrdine) => {
    if (!window.confirm('Sei sicuro di voler cancellare questo ordine? (Verrà segnato come CANCELLATO)')) return;
    
    try {
      const url = dataSelezionata !== new Date().toISOString().split('T')[0]
        ? `${API_URL}/ordini/${numeroOrdine}?data=${dataSelezionata}`
        : `${API_URL}/ordini/${numeroOrdine}`;
      
      const res = await fetch(url, {
        method: 'DELETE',
        headers: authHeader
      });
      if (res.ok) fetchOrdini();
      else alert('Errore durante la cancellazione dell\'ordine');
    } catch (error) {
      console.error('Errore cancellazione ordine:', error);
      alert('Errore durante la cancellazione dell\'ordine');
    }
  };

  const iniziaModifica = (ordine) => {
    if (ordine.cancellato) {
      alert('Non è possibile modificare un ordine cancellato');
      return;
    }

    const totaleCalcolato = ordine.alimenti.reduce((sum, a) => sum + a.prezzo, 0);
    const totaleModificatoManualmente = Math.abs(ordine.totale - totaleCalcolato) > 0.01 ? ordine.totale : null;
    
    const alimentiRaggruppati = {};
    ordine.alimenti.forEach((a, idx) => {
      const chiave = `${a.alimento_id}_${a.note || ''}`;
      if (alimentiRaggruppati[chiave]) {
        alimentiRaggruppati[chiave].quantita++;
      } else {
        alimentiRaggruppati[chiave] = {
          ...a,
          id: a.alimento_id,
          quantita: 1,
          tempId: Date.now() + idx
          // 'tipo' arriva già dal DB, non serve lookup
        };
      }
    });
    
    setOrdineInModifica({
      numero_ordine: ordine.numero_ordine,
      carrello: Object.values(alimentiRaggruppati),
      totaleManuale: totaleModificatoManualmente,
      daAsporto: ordine.da_asporto || false,
      metodoPagamento: ordine.metodo_pagamento || 'contante',
      scontoAttivo: ordine.sconto_applicato || false  // 🆕 Leggi il valore salvato nel DB
    });
  };

  const annullaModifica = () => {
    setOrdineInModifica(null);
  };

  const salvaModifica = async () => {
    // Calcola totale: se sconto attivo, applica -10% su tutto tranne bibite/dolci
    const totaleCalcolato = calcolaTotaleConSconto(
      ordineInModifica.carrello,
      ordineInModifica.scontoAttivo
    );
    const totaleFinale = ordineInModifica.totaleManuale !== null 
      ? ordineInModifica.totaleManuale 
      : totaleCalcolato;
    
    // Espandi alimenti con prezzi ORIGINALI (non scontati)
    // Lo sconto viene applicato solo al totale finale
    const alimentiEspansi = [];
    ordineInModifica.carrello.forEach(item => {
      for (let i = 0; i < item.quantita; i++) {
        alimentiEspansi.push({
          alimento_id: item.id,
          nome: item.nome,
          prezzo: item.prezzo,
          tipo: item.tipo,  // Salva il tipo per calcolo sconto futuro
          note: item.note || ''
        });
      }
    });
    
    try {
      const url = dataSelezionata !== new Date().toISOString().split('T')[0]
        ? `${API_URL}/ordini/${ordineInModifica.numero_ordine}/modifica?data=${dataSelezionata}`
        : `${API_URL}/ordini/${ordineInModifica.numero_ordine}/modifica`;
      
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alimenti: alimentiEspansi,
          totale: totaleFinale,
          da_asporto: ordineInModifica.daAsporto || false,
          metodo_pagamento: ordineInModifica.metodoPagamento || 'contante',
          sconto_applicato: ordineInModifica.scontoAttivo || false  // 🆕 Salva flag sconto nel DB
        })
      });
      
      if (res.ok) {
        setOrdineInModifica(null);
        fetchOrdini();
      } else {
        alert('Errore durante la modifica dell\'ordine');
      }
    } catch (error) {
      console.error('Errore modifica ordine:', error);
      alert('Errore durante la modifica dell\'ordine');
    }
  };

  const aggiornaNotaModifica = (tempId, note) => {
    setOrdineInModifica(prev => ({
      ...prev,
      carrello: prev.carrello.map(i =>
        i.tempId === tempId ? { ...i, note } : i
      )
    }));
  };

  const aggiornaPrezzoModifica = (tempId, prezzo) => {
    setOrdineInModifica(prev => ({
      ...prev,
      carrello: prev.carrello.map(i =>
        i.tempId === tempId ? { ...i, prezzo } : i
      )
    }));
  };

  const aggiornaQuantitaModifica = (tempId, quantita) => {
    if (quantita < 1) return;
    setOrdineInModifica(prev => ({
      ...prev,
      carrello: prev.carrello.map(i =>
        i.tempId === tempId ? { ...i, quantita } : i
      )
    }));
  };

  const aggiornaTotaleManuale = (nuovoTotale) => {
    setOrdineInModifica(prev => ({
      ...prev,
      totaleManuale: nuovoTotale
    }));
  };

  const tipiAlimenti = ['Tutti', ...new Set(alimenti.map(a => a.tipo))];

  const ordiniFiltrati = useMemo(() => {
    if (nascondiCompletati) {
      return ordiniGiorno.filter(ordine => !ordine.completato && !ordine.cancellato);
    }
    return ordiniGiorno;
  }, [ordiniGiorno, nascondiCompletati]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
          <h1 className="text-4xl font-bold text-green-700 mb-2 text-center">Farcito</h1>
          <p className="text-gray-600 text-center mb-6">Sistema Gestione Comande</p>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
            >
              Accedi
            </button>
          </form>
        </div>
      </div>
    );
  }

  const SchermaataOrdini = () => (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h1 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <ShoppingCart className="text-green-600" />
          Nuova Ordinazione
        </h1>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-700">Menu</h2>
                <div className="flex items-center gap-2">
                  <Filter size={18} className="text-gray-600" />
                  <select
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                    className="border rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {tipiAlimenti.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <MenuAlimentiConRicerca 
              alimenti={alimenti}
              filtroTipo={filtroTipo}
              onAggiungi={aggiungiAlCarrello}
            />
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Ordine Corrente</h2>
            
            <div className="mb-4">
              <NomeOrdineInput value={nomeOrdine} onUpdate={setNomeOrdine} />
            </div>

            <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">Da Asporto</span>
                <span className="text-xs text-gray-500">🥡</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={daAsporto}
                  onChange={(e) => setDaAsporto(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>

            {/* Switch Metodo Pagamento */}
            <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
              <div className="flex items-center gap-2">
                <span className="text-gray-700 font-medium">Metodo Pagamento</span>
                <span className="text-xs text-gray-500">
                  {metodoPagamento === 'contante' ? '💵' : '💳'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium transition ${metodoPagamento === 'contante' ? 'text-green-600' : 'text-gray-400'}`}>
                  Contante
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={metodoPagamento === 'carta'}
                    onChange={(e) => setMetodoPagamento(e.target.checked ? 'carta' : 'contante')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className={`text-sm font-medium transition ${metodoPagamento === 'carta' ? 'text-blue-600' : 'text-gray-400'}`}>
                  Carta
                </span>
              </div>
            </div>

            {user.role && (
              <div className={`mb-4 flex items-center justify-between rounded-lg p-3 border-2 transition-all ${
                scontoDecimi
                  ? 'bg-orange-50 border-orange-400'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center gap-2">
                  <span className={`font-medium transition ${scontoDecimi ? 'text-orange-700' : 'text-gray-700'}`}>
                    Sconto 10%
                  </span>
                  <span className="text-xs text-gray-500">🏷️</span>
                  {scontoDecimi && (
                    <span className="text-xs bg-orange-500 text-white font-bold px-2 py-0.5 rounded-full animate-pulse">
                      ATTIVO
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {scontoDecimi && (
                    <span className="text-xs text-orange-600 font-medium hidden sm:block">
                      esclusi bibite e dolci
                    </span>
                  )}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scontoDecimi}
                      onChange={(e) => setScontoDecimi(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
              </div>
            )}

            <div className="border rounded-lg p-4 min-h-96">
              {carrello.length === 0 ? (
                <p className="text-gray-400 text-center mt-8">Nessun alimento aggiunto</p>
              ) : (
                <div className="space-y-3">
                  {carrello.map((item) => (
                    <CarrelloItem
                      key={item.tempId}
                      item={item}
                      onRemove={rimuoviDalCarrello}
                      onUpdateNote={aggiornaNote}
                      onUpdatePrezzo={aggiornaPrezzo}
                      onUpdateQuantita={aggiornaQuantita}
                      isAdmin={user.role === 'admin'}
                      scontoAttivo={scontoDecimi}
                    />
                  ))}
                  <div className="pt-3 border-t-2">
                    {/* 🆕 Totale con dettaglio sconto */}
                    {scontoDecimi ? (
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-sm text-gray-500">
                          <span>Subtotale:</span>
                          <span className="line-through">
                            €{carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm text-orange-600">
                          <span>Sconto 10% applicato:</span>
                          <span>
                            -€{(
                              carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0) -
                              calcolaTotaleConSconto(carrello, true)
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xl font-bold border-t pt-1">
                          <span>Totale:</span>
                          <span className="text-orange-600">
                            €{calcolaTotaleConSconto(carrello, true).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center text-xl font-bold">
                        <span>Totale:</span>
                        <span className="text-green-600">
                          €{carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {carrello.length > 0 && (
              <button
                onClick={inviaOrdine}
                disabled={invioInCorso}
                className={`w-full mt-4 py-3 rounded-lg font-semibold transition ${
                  invioInCorso 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : scontoDecimi
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {invioInCorso ? 'Invio in corso...' : scontoDecimi ? '🏷️ Invia Ordine con Sconto 10%' : 'Invia Ordine in Cucina'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const SchermataCucina = () => (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-3">
            <ChefHat className="text-green-600" />
            Comande in Cucina
          </h1>
          
          <div className="flex items-center gap-4 flex-wrap">
            {/* 🔔 Suono nuovi ordini */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border">
              <span className="text-sm text-gray-700 font-medium">Suono nuovi ordini</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={suonoAbilitato}
                  onChange={(e) => toggleSuono(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
              {suonoAbilitato ? <Volume2 size={18} className="text-green-600" /> : <VolumeX size={18} className="text-gray-400" />}
            </div>

            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border">
              <span className="text-sm text-gray-700 font-medium">Nascondi completati</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={nascondiCompletati}
                  onChange={(e) => setNascondiCompletati(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              {nascondiCompletati ? <EyeOff size={18} className="text-gray-600" /> : <Eye size={18} className="text-gray-600" />}
            </div>

            {user.role === 'admin' && (
              <div className="flex items-center gap-2">
                <Calendar className="text-gray-600" size={20} />
                <select
                  value={dataSelezionata}
                  onChange={(e) => setDataSelezionata(e.target.value)}
                  className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value={new Date().toISOString().split('T')[0]}>Oggi</option>
                  {dateDisponibili.filter(d => d !== new Date().toISOString().split('T')[0]).map(data => (
                    <option key={data} value={data}>
                      {new Date(data + 'T00:00:00').toLocaleDateString('it-IT')}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ordiniFiltrati.length === 0 ? (
            <p className="col-span-full text-gray-400 text-center py-12">
              {nascondiCompletati && ordiniGiorno.length > 0 
                ? 'Tutti gli ordini sono stati completati! 🎉' 
                : 'Nessuna comanda per questa data'}
            </p>
          ) : (
            ordiniFiltrati.slice().reverse().map((ordine) => {
              const alimentiRaggruppati = {};
              ordine.alimenti.forEach((a) => {
                const chiave = `${a.alimento_id}_${a.note || ''}`;
                if (alimentiRaggruppati[chiave]) {
                  alimentiRaggruppati[chiave].quantita++;
                } else {
                  alimentiRaggruppati[chiave] = { ...a, quantita: 1 };
                }
              });

              return (
                <div
                  key={ordine.numero_ordine}
                  className={`border rounded-lg p-4 transition ${
                    ordine.cancellato 
                      ? 'bg-red-50 opacity-70 border-red-300' 
                      : ordine.completato 
                        ? 'bg-gray-100 opacity-60' 
                        : 'bg-white shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg font-bold text-green-600">#{ordine.numero_ordine}</span>
                        {ordine.da_asporto && (
                          <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                            🥡 DA ASPORTO
                          </span>
                        )}
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          ordine.metodo_pagamento === 'carta' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {ordine.metodo_pagamento === 'carta' ? '💳 CARTA' : '💵 CONTANTE'}
                        </span>
                        {/* 🆕 Badge sconto 10% - visibile a tutti */}
                        {ordine.sconto_applicato && (
                          <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
                            🏷️ -10%
                          </span>
                        )}
                      </div>
                      {ordine.nome_ordine && (
                        <div className="text-sm font-semibold text-gray-700 mt-1">{ordine.nome_ordine}</div>
                      )}
                      <span className="text-sm text-gray-500 block mt-1">
                        {new Date(ordine.timestamp).toLocaleTimeString('it-IT', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {ordine.cancellato && (
                        <span className="text-red-600 font-bold text-sm flex items-center gap-1 bg-red-100 px-2 py-1 rounded">
                          <XCircle size={16} /> CANCELLATO
                        </span>
                      )}
                      {ordine.completato && !ordine.cancellato && (
                        <span className="text-green-600 font-semibold text-sm flex items-center gap-1">
                          <Check size={16} /> Fatto
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-3">
                    {Object.values(alimentiRaggruppati).map((alimento, idx) => {
                      const alimentoCompleto = alimenti.find(a => a.id === alimento.alimento_id);
                      const ingredienti = alimentoCompleto?.ingredienti || [];
                      
                      return (
                        <div key={idx} className="border-b pb-2">
                          <div className="flex justify-between">
                            <div className="flex items-center gap-2">
                              {alimento.quantita > 1 && (
                                <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                                  x{alimento.quantita}
                                </span>
                              )}
                              <span className="font-semibold">{alimento.nome}</span>
                            </div>
                            <span className="text-green-600">
                              €{alimento.quantita > 1 
                                ? `${alimento.prezzo.toFixed(2)} (€${(alimento.prezzo * alimento.quantita).toFixed(2)})` 
                                : alimento.prezzo.toFixed(2)}
                            </span>
                          </div>
                          {ingredienti.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {ingredienti.join(', ')}
                            </p>
                          )}
                          {alimento.note && (
                            <p className="text-sm text-red-600 italic mt-1 font-medium">📝 {alimento.note}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold">Totale: €{ordine.totale.toFixed(2)}</span>
                    </div>
                    
                    {user.role === 'admin' && (
                      <div className="flex gap-3">
                        {ordine.cancellato ? (
                          <div className="w-full text-center text-sm text-gray-500 py-2">
                            Ordine cancellato - nessuna azione disponibile
                          </div>
                        ) : !ordine.completato ? (
                          <>
                            <button
                              onClick={() => eliminaOrdine(ordine.numero_ordine)}
                              className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2 min-h-[3rem] text-base font-semibold"
                              title="Cancella ordine"
                            >
                              <Trash2 size={20} />
                            </button>
                            
                            <button
                              onClick={() => completaOrdine(ordine.numero_ordine)}
                              className="flex-[2] bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 min-h-[3rem] text-base font-semibold"
                              title="Completa ordine"
                            >
                              <Check size={20} /> Fatto
                            </button>
                            
                            <button
                              onClick={() => iniziaModifica(ordine)}
                              className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 min-h-[3rem] text-base font-semibold"
                              title="Modifica ordine"
                            >
                              <Edit2 size={20} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => riapriOrdine(ordine.numero_ordine)}
                            className="w-full bg-yellow-600 text-white px-4 py-3 rounded-lg hover:bg-yellow-700 transition min-h-[3rem] text-base font-semibold"
                          >
                            Riapri
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const SchermataStatistiche = () => {
    const totaleOrdini = statistiche.reduce((sum, s) => sum + s.totale_ordini, 0);
    const totaleIncasso = statistiche.reduce((sum, s) => sum + s.totale_incasso, 0);
    const totaleCompletati = statistiche.reduce((sum, s) => sum + s.ordini_completati, 0);
    const mediaGiornaliera = statistiche.length > 0 ? totaleIncasso / statistiche.length : 0;
    
    const totaleContante = statistiche.reduce((sum, s) => sum + (s.totale_contante || 0), 0);
    const totaleCarta = statistiche.reduce((sum, s) => sum + (s.totale_carta || 0), 0);
    const ordiniContante = statistiche.reduce((sum, s) => sum + (s.ordini_contante || 0), 0);
    const ordiniCarta = statistiche.reduce((sum, s) => sum + (s.ordini_carta || 0), 0);
  
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <TrendingUp className="text-green-600" />
              Statistiche e Andamento
            </h1>
            
            <div className="flex gap-2">
              <button
                onClick={() => setPeriodoStats('giorno')}
                className={`px-4 py-2 rounded transition ${
                  periodoStats === 'giorno' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Oggi
              </button>
              <button
                onClick={() => setPeriodoStats('settimana')}
                className={`px-4 py-2 rounded transition ${
                  periodoStats === 'settimana' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                7 Giorni
              </button>
              <button
                onClick={() => setPeriodoStats('mese')}
                className={`px-4 py-2 rounded transition ${
                  periodoStats === 'mese' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                30 Giorni
              </button>
            </div>
          </div>
  
          <div className="flex gap-3 mb-6 border-b pb-4">
            <button
              onClick={() => setVistaStats('piu-venduti')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                vistaStats === 'piu-venduti'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Trophy size={20} />
              Più Venduti
            </button>
            
            <button
              onClick={() => setVistaStats('dettaglio')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                vistaStats === 'dettaglio'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Calendar size={20} />
              Dettaglio Giornaliero
            </button>
          </div>
  
          {vistaStats === 'piu-venduti' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                🏆 Classifica dei più venduti (escluse bibite)
              </h2>
              
              {alimentiPiuVenduti.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg mb-2">Nessun dato disponibile</p>
                  <p className="text-sm">Seleziona un periodo diverso per vedere i risultati</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {alimentiPiuVenduti.slice(0, 12).map((alimento, index) => (
                    <div 
                      key={alimento.alimento_id}
                      className={`border rounded-lg p-4 hover:shadow-lg transition ${
                        index < 3 ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400' : 'bg-gradient-to-br from-white to-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {index === 0 && <span className="text-2xl">🥇</span>}
                          {index === 1 && <span className="text-2xl">🥈</span>}
                          {index === 2 && <span className="text-2xl">🥉</span>}
                          {index > 2 && (
                            <span className="text-sm font-bold text-gray-500 bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center">
                              {index + 1}
                            </span>
                          )}
                          <div>
                            <h3 className="font-bold text-gray-800">{alimento.nome}</h3>
                            <p className="text-xs text-gray-500">{alimento.tipo}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-1 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Quantità venduta:</span>
                          <span className="font-bold text-green-600 text-lg">{alimento.quantita_venduta}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Incasso totale:</span>
                          <span className="font-bold text-blue-600">€{alimento.incasso_totale.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Prezzo medio:</span>
                          <span className="text-sm text-gray-700">€{alimento.prezzo_medio.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
  
          {vistaStats === 'dettaglio' && (
            <div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-6 shadow-md">
                  <div className="text-sm opacity-90 mb-1">Totale Ordini</div>
                  <div className="text-3xl font-bold">{totaleOrdini}</div>
                  <div className="text-xs mt-2 opacity-80">
                    💵 {ordiniContante} contante • 💳 {ordiniCarta} carta
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-md">
                  <div className="text-sm opacity-90 mb-1">Incasso Totale</div>
                  <div className="text-3xl font-bold">€{totaleIncasso.toFixed(2)}</div>
                  <div className="text-xs mt-2 opacity-80">
                    💵 €{totaleContante.toFixed(2)} • 💳 €{totaleCarta.toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-md">
                  <div className="text-sm opacity-90 mb-1">Ordini Completati</div>
                  <div className="text-3xl font-bold">{totaleCompletati}</div>
                  <div className="text-xs mt-2 opacity-80">
                    {totaleOrdini > 0 ? Math.round((totaleCompletati / totaleOrdini) * 100) : 0}% completamento
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg p-6 shadow-md">
                  <div className="text-sm opacity-90 mb-1">Media Giornaliera</div>
                  <div className="text-3xl font-bold">€{mediaGiornaliera.toFixed(2)}</div>
                  <div className="text-xs mt-2 opacity-80">
                    {totaleOrdini > 0 ? `€${(totaleIncasso / totaleOrdini).toFixed(2)} per ordine` : 'N/A'}
                  </div>
                </div>
              </div>
            <div className="overflow-x-auto pt-8">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Data</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Ordini</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Completati</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">
                      <div className="flex items-center justify-end gap-1">
                        💵 Contante
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">
                      <div className="flex items-center justify-end gap-1">
                        💳 Carta
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Incasso Tot.</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Media/Ord.</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiche.slice().reverse().map((stat) => {
                    const mediaOrdine = stat.totale_ordini > 0 
                      ? stat.totale_incasso / stat.totale_ordini 
                      : 0;
                    const isOggi = stat.data === new Date().toISOString().split('T')[0];
                    
                    return (
                      <tr 
                        key={stat.data} 
                        className={`border-b hover:bg-gray-50 ${isOggi ? 'bg-green-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium">
                            {new Date(stat.data + 'T00:00:00').toLocaleDateString('it-IT', {
                              weekday: 'short',
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                            {isOggi && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">OGGI</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {stat.totale_ordini}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-green-600 font-medium">{stat.ordini_completati}</span>
                          {stat.totale_ordini > 0 && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({Math.round(stat.ordini_completati / stat.totale_ordini * 100)}%)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-green-600">
                            €{(stat.totale_contante || 0).toFixed(2)}
                          </span>
                          {stat.totale_ordini > 0 && stat.ordini_contante > 0 && (
                            <div className="text-xs text-gray-500">
                              {Math.round((stat.ordini_contante / stat.totale_ordini) * 100)}%
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-blue-600">
                            €{(stat.totale_carta || 0).toFixed(2)}
                          </span>
                          {stat.totale_ordini > 0 && stat.ordini_carta > 0 && (
                            <div className="text-xs text-gray-500">
                              {Math.round((stat.ordini_carta / stat.totale_ordini) * 100)}%
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800">
                          €{stat.totale_incasso.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          €{mediaOrdine.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100 font-bold">
                  <tr>
                    <td className="px-4 py-3">TOTALE</td>
                    <td className="px-4 py-3 text-right">
                      {totaleOrdini}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600">{totaleCompletati}</td>
                    <td className="px-4 py-3 text-right text-green-600">
                      €{totaleContante.toFixed(2)}
                      <div className="text-xs text-gray-500 font-normal">
                        {totaleOrdini > 0 ? Math.round((ordiniContante / totaleOrdini) * 100) : 0}%
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-blue-600">
                      €{totaleCarta.toFixed(2)}
                      <div className="text-xs text-gray-500 font-normal">
                        {totaleOrdini > 0 ? Math.round((ordiniCarta / totaleOrdini) * 100) : 0}%
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800">€{totaleIncasso.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">€{(totaleOrdini > 0 ? totaleIncasso / totaleOrdini : 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl sm:text-2xl font-bold">Farcito</h1>
            
            <div className="hidden md:flex items-center gap-4">
              <span className="text-sm bg-green-700 px-3 py-1 rounded">
                {user.username} ({user.role === 'admin' ? 'Admin' : 'Utente'})
              </span>
              <button
                onClick={() => setView('ordini')}
                className={`px-4 py-2 rounded transition ${
                  view === 'ordini' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                Ordinazioni
              </button>
              <button
                onClick={() => setView('cucina')}
                className={`px-4 py-2 rounded transition ${
                  view === 'cucina' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                Cucina
              </button>
              {user.role === 'admin' && (
                <button
                  onClick={() => setView('statistiche')}
                  className={`px-4 py-2 rounded transition ${
                    view === 'statistiche' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                  }`}
                >
                  Statistiche
                </button>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 transition flex items-center gap-2"
              >
                <LogOut size={18} /> Esci
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded hover:bg-green-700 transition"
            >
              {mobileMenuOpen ? <X size={24} /> : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2">
              <div className="text-sm bg-green-700 px-3 py-2 rounded mb-3">
                {user.username} ({user.role === 'admin' ? 'Admin' : 'Utente'})
              </div>
              <button
                onClick={() => {
                  setView('ordini');
                  setMobileMenuOpen(false);
                }}
                className={`w-full px-4 py-2 rounded transition text-left ${
                  view === 'ordini' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                Ordinazioni
              </button>
              <button
                onClick={() => {
                  setView('cucina');
                  setMobileMenuOpen(false);
                }}
                className={`w-full px-4 py-2 rounded transition text-left ${
                  view === 'cucina' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                Cucina
              </button>
              {user.role === 'admin' && (
                <button
                  onClick={() => {
                    setView('statistiche');
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full px-4 py-2 rounded transition text-left ${
                    view === 'statistiche' ? 'bg-white text-green-600' : 'bg-green-500 hover:bg-green-400'
                  }`}
                >
                  Statistiche
                </button>
              )}
              <button
                onClick={logout}
                className="w-full px-4 py-2 rounded bg-red-600 hover:bg-red-700 transition flex items-center gap-2"
              >
                <LogOut size={18} /> Esci
              </button>
            </div>
          )}
        </div>
      </nav>

      {view === 'ordini' ? <SchermaataOrdini /> : view === 'cucina' ? <SchermataCucina /> : <SchermataStatistiche />}
            
      {ordineInModifica && (
        <div className="fixed inset-0 bg-white overflow-y-auto" style={{ zIndex: 9999, top: 0, left: 0, right: 0, bottom: 0, position: 'fixed' }}>
          <div className="min-h-screen">
            <div className="sticky top-0 bg-white border-b shadow-md px-6 py-4" style={{ zIndex: 10000 }}>
              <div className="max-w-7xl mx-auto flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  Modifica Ordine #{ordineInModifica.numero_ordine}
                </h2>
                <button 
                  onClick={annullaModifica} 
                  className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <X size={28} />
                </button>
              </div>
            </div>
      
            <div className="max-w-7xl mx-auto p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border shadow-sm p-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">📋 Menu Disponibile</h3>
                  
                  <MenuAlimentiConRicerca 
                    alimenti={alimenti}
                    filtroTipo="Tutti"
                    onAggiungi={(alimento) => {
                      const esistente = ordineInModifica.carrello.find(item =>
                        item.id === alimento.id &&
                        item.prezzo === alimento.prezzo &&
                        !item.note
                      );
                      
                      if (esistente) {
                        setOrdineInModifica({
                          ...ordineInModifica,
                          carrello: ordineInModifica.carrello.map(i =>
                            i.tempId === esistente.tempId
                              ? { ...i, quantita: i.quantita + 1 }
                              : i
                          )
                        });
                      } else {
                        setOrdineInModifica({
                          ...ordineInModifica,
                          carrello: [...ordineInModifica.carrello, { ...alimento, note: '', quantita: 1, tempId: Date.now() }]
                        });
                      }
                    }}
                  />
                </div>
      
                <div className="bg-white rounded-lg border shadow-sm p-6">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">🛒 Ordine Modificato</h3>
                  
                  {ordineInModifica.carrello.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p>Nessun alimento nell'ordine</p>
                      <p className="text-sm mt-2">Aggiungi almeno un alimento dal menu</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto mb-4">
                      {ordineInModifica.carrello.map((item) => (
                        <ModificaCarrelloItem
                          key={item.tempId}
                          item={item}
                          onRemove={(tempId) => setOrdineInModifica({
                            ...ordineInModifica,
                            carrello: ordineInModifica.carrello.filter(i => i.tempId !== tempId)
                          })}
                          onUpdateNote={aggiornaNotaModifica}
                          onUpdatePrezzo={aggiornaPrezzoModifica}
                          onUpdateQuantita={aggiornaQuantitaModifica}
                          scontoAttivo={ordineInModifica.scontoAttivo || false}
                        />
                      ))}
                    </div>
                  )}
      
                  <div className="pt-4 border-t-2">
                    <div className="flex justify-between font-bold text-xl items-center mb-4">
                      <span>Totale:</span>
                      {ordineInModifica.totaleManuale !== null ? (
                        <ModificaTotaleOrdine
                          totale={ordineInModifica.totaleManuale}
                          onUpdate={aggiornaTotaleManuale}
                        />
                      ) : (
                        // 🆕 Mostra totale con sconto se attivo
                        <div className="text-right">
                          {ordineInModifica.scontoAttivo ? (
                            <div>
                              <div className="text-sm text-gray-400 line-through font-normal">
                                €{ordineInModifica.carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0).toFixed(2)}
                              </div>
                              <span className="text-orange-600">
                                €{calcolaTotaleConSconto(ordineInModifica.carrello, true).toFixed(2)}
                              </span>
                            </div>
                          ) : (
                            <ModificaTotaleOrdine
                              totale={ordineInModifica.carrello.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0)}
                              onUpdate={aggiornaTotaleManuale}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    {ordineInModifica.totaleManuale !== null && (
                      <p className="text-xs text-orange-600 mb-3 text-right">
                        ⚠️ Totale modificato manualmente
                      </p>
                    )}

                    {/* 🆕 Switch sconto 10% nel modal modifica - SOLO ADMIN */}
                    <div className={`mb-4 flex items-center justify-between rounded-lg p-3 border-2 transition-all ${
                      ordineInModifica.scontoAttivo
                        ? 'bg-orange-50 border-orange-400'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium transition ${ordineInModifica.scontoAttivo ? 'text-orange-700' : 'text-gray-700'}`}>
                          Sconto 10%
                        </span>
                        <span className="text-xs text-gray-500">🏷️</span>
                        {ordineInModifica.scontoAttivo && (
                          <span className="text-xs bg-orange-500 text-white font-bold px-2 py-0.5 rounded-full">
                            ATTIVO
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {ordineInModifica.scontoAttivo && (
                          <span className="text-xs text-orange-600 font-medium hidden sm:block">
                            esclusi bibite e dolci
                          </span>
                        )}
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ordineInModifica.scontoAttivo || false}
                            onChange={(e) => setOrdineInModifica({
                              ...ordineInModifica,
                              scontoAttivo: e.target.checked,
                              totaleManuale: null  // reset totale manuale quando si cambia sconto
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                    </div>
      
                    <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700 font-medium">Da Asporto</span>
                        <span className="text-xs text-gray-500">🥡</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ordineInModifica.daAsporto || false}
                          onChange={(e) => setOrdineInModifica({
                            ...ordineInModifica,
                            daAsporto: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
      
                    <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700 font-medium">Metodo Pagamento</span>
                        <span className="text-xs text-gray-500">
                          {ordineInModifica.metodoPagamento === 'contante' ? '💵' : '💳'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium transition ${ordineInModifica.metodoPagamento === 'contante' ? 'text-green-600' : 'text-gray-400'}`}>
                          Contante
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ordineInModifica.metodoPagamento === 'carta'}
                            onChange={(e) => setOrdineInModifica({
                              ...ordineInModifica,
                              metodoPagamento: e.target.checked ? 'carta' : 'contante'
                            })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <span className={`text-sm font-medium transition ${ordineInModifica.metodoPagamento === 'carta' ? 'text-blue-600' : 'text-gray-400'}`}>
                          Carta
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
      
              <div className="sticky bottom-0 bg-white border-t shadow-lg mt-6 p-6" style={{ zIndex: 10000 }}>
                <div className="max-w-7xl mx-auto flex gap-4">
                  <button
                    onClick={annullaModifica}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition"
                  >
                    ❌ Annulla
                  </button>
                  <button
                    onClick={salvaModifica}
                    className={`flex-1 text-white py-3 rounded-lg font-semibold transition ${
                      ordineInModifica.scontoAttivo
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    disabled={ordineInModifica.carrello.length === 0}
                  >
                    {ordineInModifica.scontoAttivo ? '🏷️ Salva con Sconto 10%' : '✅ Salva Modifiche'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}